import { FilterModel, FilterStructureItemModel, FilterStructureModel, FilterTypeEnum, QueryOperator } from '@/libs/Api';
import { filtersStore } from '@/libs/filters/+state/store';
import { CrudAction, CrudResponseAction } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { CrudDataQuery } from '@/libs/core/+state/models/crud-service';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';

// Klíč pro localStorage – vybraný filtr pro TPM Analytics (sdílený napříč stránkami)
const LOCAL_KEY = 'tpm.analytics.selectedFilter';

/**
 * Spočítá expiraci jako maximum z: (dnes 23:59:59.999) a (teď + 8 hodin)
 * – tzn. platnost na dnešní den, ale nejméně 8 hodin od posledního použití
 */
function computeExpiryMs(nowMs: number): number {
    const now = new Date(nowMs);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
    const plus8h = nowMs + 8 * 60 * 60 * 1000;
    return Math.max(endOfDay, plus8h);
}

export function setSelectedFilterLocal(filterId: string): void {
    const now = Date.now();
    const payload = { id: filterId, expiresAt: computeExpiryMs(now) };
    try {
        localStorage.setItem(LOCAL_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

export function getSelectedFilterLocal(): string | null {
    try {
        const raw = localStorage.getItem(LOCAL_KEY);
        if (!raw) return null;
        const obj = JSON.parse(raw || '{}') as { id?: string; expiresAt?: number };
        if (!obj?.id || !obj?.expiresAt) return null;
        const now = Date.now();
        if (now > obj.expiresAt) {
            localStorage.removeItem(LOCAL_KEY);
            return null;
        }
        // sliding expirace – prodloužíme při každém použití
        obj.expiresAt = computeExpiryMs(now);
        localStorage.setItem(LOCAL_KEY, JSON.stringify(obj));
        return obj.id;
    } catch {
        return null;
    }
}

export function clearSelectedFilterLocal(): void {
    try {
        localStorage.removeItem(LOCAL_KEY);
    } catch {
        /* ignore */
    }
}

// Serializace stavu filtrů do FilterStructureModel – použijeme jedinou položku s JSONem
export function packSettings(state: any): FilterStructureModel {
    const item: FilterStructureItemModel = {
        propertyName: 'tpmAnalytics',
        operator: QueryOperator.Wrap,
        comparand: JSON.stringify(state || {}),
    };
    return { items: [item] };
}

export function unpackSettings(settings?: FilterStructureModel | null): any {
    const it = settings?.items && settings.items.length > 0 ? settings.items[0] : null;
    if (!it?.comparand) return {};
    try {
        return JSON.parse(it.comparand);
    } catch {
        return {};
    }
}

// Serverové API přes filtersStore
export async function listSavedFilters(): Promise<FilterModel[]> {
    const key = 'tpmAnalyticsSaved';
    const query = createCrudQueryPayload<FilterModel>(
        undefined,
        [
            { field: 'filterType', op: 'eq', comparand: FilterTypeEnum.TpmAnalytics },
            { field: 'area', op: 'eq', comparand: 'TpmAnalytics' },
        ],
        undefined,
        undefined,
        key
    );
    await filtersStore.dispatch(CrudAction.GetAll, query);
    const list = filtersStore.useGetter(CrudGetter.Data, key) as FilterModel[];
    return Array.isArray(list) ? list : [];
}

export async function getFilterById(id: string): Promise<FilterModel | null> {
    const key = 'tpmAnalyticsDetail';
    try {
        await filtersStore.dispatch(CrudAction.Get, { key, id, silent: true });
        const item = filtersStore.useGetter(CrudGetter.Detail, key) as FilterModel | null;
        return item || null;
    } catch {
        return null;
    }
}

export async function saveNewFilter(name: string, state: any, isPublic = false): Promise<FilterModel | null> {
    const model: FilterModel = {
        name,
        isPublic,
        filterType: FilterTypeEnum.TpmAnalytics,
        area: 'TpmAnalytics',
        settings: packSettings(state),
    } as any;
    const key = 'tpmAnalyticsCreate';
    await filtersStore.dispatch(CrudAction.Create, { key, item: model });
    const created = filtersStore.useGetter(CrudGetter.Detail, key) as FilterModel | null;
    return created || null;
}

export async function updateFilter(model: FilterModel, state: any): Promise<FilterModel | null> {
    const updated: FilterModel = { ...model, settings: packSettings(state) } as any;
    const key = 'tpmAnalyticsUpdate';
    await filtersStore.dispatch(CrudAction.Update, { key, item: updated });
    return updated;
}

export async function deleteFilter(id: string): Promise<void> {
    const key = 'tpmAnalyticsDelete';
    await filtersStore.dispatch(CrudAction.Delete, { key, id });
}
